import {
  PresignConversationAttachmentResponseSchema,
  type PresignConversationAttachmentBody,
  type PresignConversationAttachmentResponse,
} from '@zrsystem/contracts';

import { getHttpClient } from '@/shared/api/http-client.js';
import { uploadFileToPresignedUrl } from '@/shared/api/storage.api.js';
import { HttpError } from '@/shared/api/types.js';

const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);
const ALLOWED_VIDEO_TYPES = new Set(['video/mp4']);
const ALLOWED_AUDIO_TYPES = new Set([
  'audio/mp4',
  'audio/mpeg',
  'audio/aac',
  'audio/webm',
  'audio/m4a',
]);

/** BR-CONV-13: same use case as consumer; endpoint accepts staff participant JWT. */
export async function presignStaffConversationAttachment(
  conversationId: string,
  body: PresignConversationAttachmentBody,
): Promise<PresignConversationAttachmentResponse> {
  const raw = await getHttpClient().post<unknown>(
    `/api/consumer/conversations/${conversationId}/attachments/presign-upload`,
    body,
  );
  return PresignConversationAttachmentResponseSchema.parse(raw);
}

/** Upload a chat attachment (image, video/mp4 or audio) and return its storage key + message kind. */
export async function uploadStaffChatMedia(
  conversationId: string,
  file: File,
): Promise<{ storageKey: string; contentType: string; kind: 'image' | 'video' | 'audio' }> {
  const contentType = file.type;
  const isImage = ALLOWED_IMAGE_TYPES.has(contentType);
  const isVideo = ALLOWED_VIDEO_TYPES.has(contentType);
  const isAudio = ALLOWED_AUDIO_TYPES.has(contentType);
  if (!isImage && !isVideo && !isAudio) {
    throw new HttpError({
      status: 400,
      code: 'OBJECT_STORAGE_INVALID_CONTENT_TYPE',
      message: 'Only JPEG, PNG, WebP, GIF images, MP4 videos or allowed audio types are allowed',
    });
  }
  const presign = await presignStaffConversationAttachment(conversationId, {
    contentType: contentType as PresignConversationAttachmentBody['contentType'],
    contentLength: file.size,
  });
  await uploadFileToPresignedUrl(presign.uploadUrl, file);
  return {
    storageKey: presign.storageKey,
    contentType,
    kind: isAudio ? 'audio' : isVideo ? 'video' : 'image',
  };
}
